<template>
    <div>
        <CompanySettingsDisabledWarning v-if="!canEditOwnerLockedSettings" />
        <div class="title mb-5">{{ $t('Login options') }}</div>
        <div
            v-for="(option, index) in loginOptions"
            :key="index"
            class="mb-2"
        >
            <switch-button
                :label="$t(option.label)"
                :value="settings[option.value + '_enabled']"
                :disabled="!canEditOwnerLockedSettings"
                @change="setSettingsValue(option.value + '_enabled', $event)"
            />

            <div
                v-if="settings.google_enabled && option.value === 'google' && !current_account.google_id"
                class="ml-1 pl-12 mt-2"
            >
                <v-row no-gutters>
                    <v-col cols="6">
                        <LoginIntegrationButton
                            :integration-type="authModule.AUTH_METHODS.GOOGLE"
                            :loading="loader.isLoading"
                            :disabled="!canEditOwnerLockedSettings"
                            @click="googleLogin"
                        />
                    </v-col>
                </v-row>
            </div>

            <div
                v-if="settings.microsoft_enabled && option.value === 'microsoft'"
                class="ml-1 pl-12 mt-2"
            >
                <v-row v-if="!current_account.microsoft_id" no-gutters>
                    <v-col cols="6" class="mb-4">
                        <LoginIntegrationButton
                            :integration-type="authModule.AUTH_METHODS.MICROSOFT"
                            :loading="loader.isLoading"
                            :disabled="!canEditOwnerLockedSettings"
                            @click="msLogin"
                        />
                    </v-col>
                </v-row>
                <v-row v-else no-gutters>
                    <v-col cols="6" class="pr-1">
                        <text-field
                            :value="settings.microsoft_tenant_id"
                            :label="$t('Tenant ID (optional)')"
                            :disabled="!canEditOwnerLockedSettings"
                            :tooltip="$t('Add Active Directory tenant ID, if only users belonging to this tenant are allowed to connect')"
                            @input="setSettingsValue('microsoft_tenant_id', $event)"
                        />
                    </v-col>
                    <v-col cols="12">
                        <a :href="HelpModule.getHelpLink('microsoft_ids')" target="_blank" class="primary--text body-2">{{ $t('How to find ID’s?') }}</a>
                    </v-col>
                </v-row>
            </div>
            <div
                v-if="settings.xero_enabled && option.value === 'xero' && !current_account.xero_id"
                class="ml-1 pl-12 mt-2"
            >
                <v-row no-gutters>
                    <v-col cols="6">
                        <LoginIntegrationButton
                            :integration-type="authModule.AUTH_METHODS.XERO"
                            :loading="loader.isLoading"
                            :disabled="!canEditOwnerLockedSettings"
                            @click="xeroLogin"
                        />
                    </v-col>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import AppStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import CompanyInfo from "@/views/AppStore/Company/Overview/CompanyInfo";
import SubscriptionInfo from "@/views/AppStore/Company/Overview/SubscriptionInfo";
import { $t } from "@/i18n";
import loginOptions from "@/config/appStore/company/loginOptions.json";
import HelpModule from "@/modules/help/help";
import AppModule from "@/store/modules/app";
import AccountModule from "@/store/modules/account";
import DialogModule from "@/store/modules/dialog";
import APIConnect from "@/api/account_setup/connect";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import { startLoginProcess } from "@/modules/auth/microsoftAuth";
import LoginIntegrationButton from "@/components/Buttons/LoginIntegrationButton";
import authModule from "@/modules/auth/auth";
import Loader from "@/utility/loader";
import GoogleLogin from "@/composables/GoogleLogin";
import Vue from 'vue';
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import CompanySettingsDisabledWarning from "@/components/Company/CompanySettingsDisabledWarning";

export default {
    name: "LoginOptions",
    components: {
        CompanyInfo,
        SubscriptionInfo,
        LoginIntegrationButton,
        CompanySettingsDisabledWarning
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        },
        settings: {
            type: Object,
            required: true
        },
        canEditOwnerLockedSettings: {
            type: Boolean,
            required: true
        }
    },
    setup(props, ctx) {
        const {appStoreDialogGetters} = AppStoreDialog()
        const {current_account} = AccountModule();
        const appModule = AppModule();
        const {showFeedbackNotification} = FeedbackNotificationModule();
        const {promptGoogleToken} = GoogleLogin();

        const loader = reactive(new Loader());

        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        });

        const setSettingsValue = async (key, value) => {
            const settingsCopy = {...props.settings}

            settingsCopy[key] = value

            ctx.emit('set-settings', settingsCopy)
        }

        const onMicrosoftConnected = async (microsoft_id) => {
            if (!microsoft_id) {
                return
            }

            try {
                await APIConnect.connectMicrosoftId(microsoft_id)
                current_account.value.microsoft_id = microsoft_id
                showFeedbackNotification($t('Microsoft connected'))
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const googleLogin = async () => {
            promptGoogleToken(async (data) => {
                try {
                    await APIConnect.connectGoogleId(data.credential)
                    current_account.value.google_id = data.credential
                    showFeedbackNotification($t('Google connected'))
                } catch (e) {
                    errorDialog.showError(e)
                }
            })
        }

        const msLogin = async () => {
            loader.withLoader(async () => {
                const loginResponse = await startLoginProcess(appModule.azure_ad_app_id.value, current_account.value.email)

                if (loginResponse) {
                    current_account.value.microsoft_token = loginResponse['idToken']
                    await onMicrosoftConnected(loginResponse['idToken'])
                }
            })
        }

        const xeroLogin = () => {
            const redirectUrl = encodeURIComponent(`/store/${selectedAccount.value.id}/?open_app_name=company&open_app_tab=security&open_app_run=enablexero`)
            window.location = `/api/xero/connect?connect_redirect_url=${redirectUrl}`
        }

        return {
            selectedAccount,
            loginOptions,
            setSettingsValue,
            HelpModule,
            current_account,
            onMicrosoftConnected,
            msLogin,
            loader,
            googleLogin,
            xeroLogin,
            authModule,
        };
    }
};
</script>
